import { DateTime } from 'luxon';
import { AddOnStatus, IssueKind, JobEventType, JobStatus } from '../../../__generated__/graphql';
import type { IJobTimelineFieldsFragment } from '../../services/serviceService/__generated__/queries.generated';

/**
 * Reading a job's timeline (spec 019 W19, W21, W22): the checklist with its
 * progress, events in words, issues, strikes and their appeals, and which
 * check-ins the worker can send next. Pure; the server decides.
 */

export type JobTimeline = IJobTimelineFieldsFragment;
export type JobEvent = JobTimeline['events'][number];
export type JobAddOn = JobTimeline['addOns'][number];

export interface ChecklistItem {
  code: string;
  label: string;
  requiresPhoto: boolean;
  done: boolean;
  photoCount: number;
  photosNeeded: number;
}

/** The checklist JSON (`done`, `photoCount`, `photosNeeded` per item) as typed rows. */
export function checklistItems(checklist: unknown): ChecklistItem[] {
  if (!Array.isArray(checklist)) return [];
  return checklist
    .filter(item => item && typeof item.code === 'string')
    .map((item: any) => ({
      code: item.code,
      label: String(item.label ?? item.code),
      requiresPhoto: Boolean(item.requiresPhoto),
      done: Boolean(item.done),
      photoCount: Number(item.photoCount ?? 0),
      photosNeeded: Number(item.photosNeeded ?? 0),
    }));
}

export function checklistSummary(items: readonly ChecklistItem[]): string {
  const done = items.filter(i => i.done).length;
  return `${done} of ${items.length} done`;
}

/** Labels of the codes `CHECKLIST_INCOMPLETE` lists in `details.missing`. */
export function missingLabels(missing: unknown, items: readonly ChecklistItem[]): string[] {
  if (!Array.isArray(missing)) return [];
  return missing.map(code => items.find(i => i.code === code)?.label ?? String(code));
}

export const ISSUE_LABELS: Record<IssueKind, string> = {
  [IssueKind.Damage]: 'Damage',
  [IssueKind.PartyOrMess]: 'Party or a big mess',
  [IssueKind.LeftItem]: 'Something left behind',
  [IssueKind.MissingSupplies]: 'Missing supplies',
  [IssueKind.NoAccess]: 'Could not get in',
  [IssueKind.NotAsDescribed]: 'Not as described',
  [IssueKind.Other]: 'Other',
};

const EVENT_LABELS: Partial<Record<JobEventType, string>> = {
  [JobEventType.EnRoute]: 'On the way',
  [JobEventType.Arrived]: 'Arrived',
  [JobEventType.Started]: 'Started',
  [JobEventType.ChecklistItem]: 'Checked off',
  [JobEventType.Photo]: 'Photo',
  [JobEventType.Issue]: 'Problem reported',
  [JobEventType.Completed]: 'Finished',
  [JobEventType.AccessViewed]: 'Opened the door code',
  [JobEventType.AddOnRequested]: 'Asked for an extra',
  [JobEventType.AddOnResponded]: 'Extra answered',
  [JobEventType.CustomerConfirmed]: 'Confirmed by the customer',
  [JobEventType.AutoConfirmed]: 'Confirmed automatically',
  [JobEventType.DisputeOpened]: 'Problem sent for review',
  [JobEventType.DisputeResolved]: 'Review closed',
  [JobEventType.LateCancel]: 'Late cancellation',
  [JobEventType.NoShow]: 'No-show',
  [JobEventType.StrikeAppealed]: 'Strike appealed',
  [JobEventType.StrikeResolved]: 'Strike reviewed',
  [JobEventType.CapReached]: 'Handover limit reached',
  [JobEventType.CapExtended]: 'Handover limit extended',
};

/** "Photo: Kitchen", "Problem reported: Damage, broken lamp". */
export function eventLabel(
  event: Pick<JobEvent, 'type' | 'checklistItemCode' | 'payload'>,
  items: readonly ChecklistItem[] = [],
): string {
  const base = EVENT_LABELS[event.type] ?? event.type;
  const item = event.checklistItemCode ? items.find(i => i.code === event.checklistItemCode) : null;
  if (item) return `${base}: ${item.label}`;
  if (event.type === JobEventType.Issue) {
    const issue = issueOf(event);
    return issue ? `${base}: ${issue.label}${issue.note ? `, ${issue.note}` : ''}` : base;
  }
  return base;
}

export function issueOf(
  event: Pick<JobEvent, 'type' | 'payload'>,
): { label: string; note: string | null } | null {
  if (event.type !== JobEventType.Issue) return null;
  const payload = (event.payload ?? {}) as Record<string, any>;
  const kind = String(payload.issueKind ?? '').toUpperCase() as IssueKind;
  return {
    label: ISSUE_LABELS[kind] ?? 'Other',
    note: typeof payload.note === 'string' && payload.note.trim() ? payload.note.trim() : null,
  };
}

/** Events the customer reads, oldest first, voided ones left out. */
export function liveEvents(events: readonly JobEvent[]): JobEvent[] {
  return events
    .filter(e => !e.voidedAt)
    .slice()
    .sort(
      (a, b) => Date.parse(a.capturedAt ?? a.receivedAt) - Date.parse(b.capturedAt ?? b.receivedAt),
    );
}

export function eventTime(
  event: Pick<JobEvent, 'capturedAt' | 'receivedAt'>,
  zone: string,
): string {
  return DateTime.fromISO(event.capturedAt ?? event.receivedAt, { zone }).toFormat('ccc h:mm a');
}

// ─── strikes (D17) ──────────────────────────────────────────────────────────

const STRIKE_TYPES = new Set<JobEventType>([JobEventType.LateCancel, JobEventType.NoShow]);

export type StrikeState = 'open' | 'appealed' | 'removed' | 'upheld';

export interface StrikeView {
  event: JobEvent;
  label: string;
  state: StrikeState;
}

/** The strikes on a job with where their appeal stands (from the appeal and decision events). */
export function strikesOf(events: readonly JobEvent[]): StrikeView[] {
  return events
    .filter(e => STRIKE_TYPES.has(e.type))
    .map(event => {
      const linked = (type: JobEventType) =>
        events.find(
          e => e.type === type && String((e.payload as any)?.strikeEventId) === String(event.id),
        );
      const decision = linked(JobEventType.StrikeResolved);
      const appeal = linked(JobEventType.StrikeAppealed);
      const state: StrikeState = event.voidedAt
        ? 'removed'
        : decision
          ? (decision.payload as any)?.upheld === false
            ? 'removed'
            : 'upheld'
          : appeal
            ? 'appealed'
            : 'open';
      return { event, label: EVENT_LABELS[event.type] ?? event.type, state };
    });
}

/** An admin strike row's payload (`bookingId`, `workerCarId`, `appeal`, `decision`). */
export function strikePayload(payload: unknown): {
  bookingId: string | null;
  workerCarId: string | null;
  appealNote: string | null;
  appealFileKey: string | null;
  decided: boolean;
  upheld: boolean | null;
} {
  const p = (payload ?? {}) as Record<string, any>;
  return {
    bookingId: p.bookingId ?? null,
    workerCarId: p.workerCarId ?? null,
    appealNote: p.appeal?.note ?? null,
    appealFileKey: p.appeal?.fileKey ?? null,
    decided: Boolean(p.decision),
    upheld: p.decision ? p.decision.upheld !== false : null,
  };
}

// ─── the worker's next check-ins (mirrors backend `jobStep`) ────────────────

const ON_SITE = new Set<JobStatus>([JobStatus.Arrived, JobStatus.InProgress]);
const DONE = new Set<JobStatus>([
  JobStatus.Completed,
  JobStatus.CustomerConfirmed,
  JobStatus.Disputed,
  JobStatus.Resolved,
]);

export interface WorkerActions {
  enRoute: boolean;
  arrived: boolean;
  started: boolean;
  checklist: boolean;
  issue: boolean;
  completed: boolean;
}

export function workerActions(current: JobStatus | null | undefined): WorkerActions {
  const status = current ?? JobStatus.Scheduled;
  return {
    enRoute: status === JobStatus.Scheduled,
    arrived: status === JobStatus.Scheduled || status === JobStatus.EnRoute,
    started: status === JobStatus.Arrived,
    checklist: ON_SITE.has(status),
    issue: !DONE.has(status),
    completed: ON_SITE.has(status),
  };
}

// ─── the customer's actions after the work (W21) ────────────────────────────

export const canConfirm = (jobStatus?: JobStatus | null) => jobStatus === JobStatus.Completed;
export const canDispute = canConfirm;

export function canAskRefund(
  jobStatus: JobStatus | null | undefined,
  refundWindowEndsAt: string | null | undefined,
  now = Date.now(),
): boolean {
  if (jobStatus !== JobStatus.CustomerConfirmed && jobStatus !== JobStatus.Resolved) return false;
  return Boolean(refundWindowEndsAt) && Date.parse(refundWindowEndsAt!) > now;
}

export const pendingAddOns = (addOns: readonly JobAddOn[]) =>
  addOns.filter(a => a.status === AddOnStatus.Requested);

export const ADD_ON_STATUS_LABELS: Record<AddOnStatus, string> = {
  [AddOnStatus.Requested]: 'Waiting for you',
  [AddOnStatus.Approved]: 'Approved',
  [AddOnStatus.AutoApproved]: 'Approved automatically',
  [AddOnStatus.Rejected]: 'Declined',
  [AddOnStatus.Expired]: 'Not answered in time',
};

/** Longer handover limits the customer can pick: 15-minute steps up to 12 hours (D30). */
export function handoverCapOptions(currentMinutes: number): number[] {
  const out: number[] = [];
  for (let m = Math.ceil((currentMinutes + 1) / 15) * 15; m <= 720; m += 15) out.push(m);
  return out;
}

export function minutesLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h ? `${h} h` : '', m ? `${m} min` : ''].filter(Boolean).join(' ') || '0 min';
}

/** When the worker finished, from the timeline when the booking is not at hand. */
export function completedAtOf(events: readonly JobEvent[]): string | null {
  const done = events.find(e => e.type === JobEventType.Completed && !e.voidedAt);
  return done ? (done.receivedAt ?? done.capturedAt ?? null) : null;
}

/** Where a strike row (admin or Provider list) stands, from its own payload. */
export function strikeRowState(event: {
  voidedAt?: string | null;
  payload?: unknown;
}): StrikeState {
  const p = strikePayload(event.payload);
  if (event.voidedAt || (p.decided && p.upheld === false)) return 'removed';
  if (p.decided) return 'upheld';
  if (p.appealNote) return 'appealed';
  return 'open';
}
